import { LUCAN, UNSLEEPING } from '@somnhold/content';
import { ANGLE_STEPS, cosA, dist2, iatan2, idiv, imax, imin, mulBp, sinA, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { damageHero, ev, spawnEnemy } from '../combat.ts';
import { by, heroesNear, held, holdable, hurt, nearestHero, said, standing, type Kit } from './kit.ts';

/*
 * Prior Lucan (cleric, shadow): he prayed in the chapel to the end, and nobody came. He shoots shadow from afar, his
 * unanswered prayer mends the nightmares around him, every one falling near him leaves him a soul, his whip curses
 * three heroes at once (they heal half), he raises his souls as skeletons, and the Empty Chapel heals and wards every
 * nightmare in it and strikes and frightens every hero. Kill what he raises, or he raises it again.
 */

// cds: whip, raise. k: souls, the prayer's beat.
const WHIP = 0;
const RAISE = 1;
const SOULS = 0;
const PRAY = 1;

/** A hero he can see (not hidden). */
const seen = (h: Hero): boolean => !((h.hidden ?? 0) > 0);

/** His blow: a bolt of shadow at the hero who challenges him, or the nearest; he stands to shoot. */
const bolt = (s: BattleState, e: Enemy): boolean => {
  let h: Hero | null = e.taunter ? (s.heroes.find((o) => o.id === e.taunter) ?? null) : null;
  if (!h || !standing(h) || !withinRange(e.x, e.y, h.x, h.y, LUCAN.reach)) h = nearestHero(s, e, LUCAN.reach, seen);
  if (!h) return false;
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  e.anim = e.anim === 'walk' ? 'idle' : e.anim;
  if (e.cd > 0) return true;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: h.x, ty: h.y, kind: 'lucanBolt' });
  lucan.strike(s, e, h, e.damage);
  return true;
};

/** The vigil whip: a chain from him to the nearest hero, then hero to hero; each struck and cursed. */
const whip = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const w = LUCAN.whip;
  const first = nearestHero(s, e, w.range, seen);
  if (!first) {
    u.cds[WHIP] = 6;
    return;
  }
  const hit: Hero[] = [first];
  while (hit.length < w.heroes) {
    const from = hit[hit.length - 1] as Hero;
    let best: Hero | null = null;
    let bd = 0;
    for (const h of s.heroes) {
      if (!standing(h) || hit.includes(h)) continue;
      const d = dist2(from.x, from.y, h.x, h.y);
      if (d > w.jump * w.jump) continue;
      if (!best || d < bd) {
        best = h;
        bd = d;
      }
    }
    if (!best) break;
    hit.push(best);
  }
  let px = e.x;
  let py = e.y;
  for (const h of hit) {
    ev(s, { t: 'beam', x: px, y: py, tx: h.x, ty: h.y, kind: 'lucanWhip' });
    px = h.x;
    py = h.y;
    hurt(s, e, u, h, w.damage, w.maxHpBp, 'shadow');
    if (!standing(h)) continue;
    h.curse = imax(h.curse ?? 0, w.curse);
    h.curseBp = w.healBp;
  }
  u.cds[WHIP] = w.every;
  e.facing = iatan2(first.y - e.y, first.x - e.x);
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Whip', first.x, first.y);
};

/** `n` skeletons get up in a ring around him. */
const raise = (s: BattleState, e: Enemy, n: number): void => {
  const r = LUCAN.raise.ring;
  const turn0 = (s.tick * 97) % ANGLE_STEPS;
  for (let i = 0; i < n; i++) {
    const a = (turn0 + idiv(ANGLE_STEPS * i, n)) % ANGLE_STEPS;
    const g = spawnEnemy(s, 'skeleton', e.tier >= 2 ? 2 : 1, false, e.sector, e.x + idiv(r * cosA(a), 10000), e.y + idiv(r * sinA(a), 10000));
    g.next = e.next;
    ev(s, { t: 'boom', x: g.x, y: g.y, r: 500, kind: 'raise' });
  }
  ev(s, { t: 'boom', x: e.x, y: e.y, r: r + 500, kind: 'lucanRaise' });
};

const hurtNear = (s: BattleState, e: Enemy, r: number): number => {
  let n = 0;
  for (const o of s.enemies) if (o !== e && o.hp > 0 && o.hp < o.maxHp && o.kind !== 'boneWall' && withinRange(e.x, e.y, o.x, o.y, r)) n++;
  return n;
};

export const lucan: Kit = {
  init: (_s, _e, u) => {
    u.cds = [idiv(LUCAN.whip.every, 2), idiv(LUCAN.raise.every, 2)];
    u.k = [0, 0];
  },
  act: (s, e, u) => {
    if (u.cds[WHIP] === 0) whip(s, e, u);
    if (u.cds[RAISE] === 0 && (u.k[SOULS] as number) >= LUCAN.raise.min) {
      raise(s, e, u.k[SOULS] as number);
      u.k[SOULS] = 0;
      u.cds[RAISE] = LUCAN.raise.every;
      e.anim = 'cast';
      e.animT = 0;
      said(s, e, u, 'Raise');
      return true;
    }
    // With the Chapel ready and nobody in it, he walks into the fight instead of shooting from afar.
    if (u.rage >= UNSLEEPING.rageMax) return false;
    return bolt(s, e);
  },
  // The Chapel around him: worth it with a hero in it, two wounded nightmares, or (from 33 %) souls to raise.
  aim: (s, e, u) => {
    const c = LUCAN.chapel;
    if (heroesNear(s, e.x, e.y, c.radius).length === 0 && hurtNear(s, e, c.radius) < 2 && !(e.phase >= 2 && (u.k[SOULS] as number) > 0)) return false;
    u.castA = e.facing;
    u.spots = [{ x: e.x, y: e.y, r: c.radius }];
    said(s, e, u, 'Chapel');
    return true;
  },
  land: (s, e, u) => {
    const c = LUCAN.chapel;
    for (const o of s.enemies) {
      if (o === e || o.hp <= 0 || o.kind === 'boneWall' || !withinRange(e.x, e.y, o.x, o.y, c.radius)) continue;
      const got = o.maxHp - o.hp;
      o.hp = o.maxHp;
      o.ward = imax(o.ward ?? 0, mulBp(o.maxHp, c.wardBp));
      ev(s, { t: 'mend', from: e.id, id: o.id, amount: got, x: o.x, y: o.y });
    }
    for (const h of s.heroes) {
      if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, c.radius)) continue;
      hurt(s, e, u, h, c.damage, c.maxHpBp, 'shadow');
      if (!holdable(s, h)) continue;
      h.fear = imax(h.fear ?? 0, c.fear);
      held(s, h);
    }
    if (e.phase >= 2 && (u.k[SOULS] as number) > 0) raise(s, e, u.k[SOULS] as number);
    ev(s, { t: 'boom', x: e.x, y: e.y, r: c.radius, kind: 'lucanChapel' });
  },
  strike: (s, e, h, dmg) => {
    damageHero(s, h, by(h, dmg, LUCAN.blowMaxHpBp), e, 'shadow', true);
  },
  // The unanswered prayer: every beat, the other nightmares near him mend (twice as much from 66 %).
  status: (s, e, u) => {
    const p = LUCAN.prayer;
    u.k[PRAY] = (u.k[PRAY] as number) + 1;
    if ((u.k[PRAY] as number) < p.every) return;
    u.k[PRAY] = 0;
    const bp = e.phase >= 1 ? mulBp(p.healBp, p.phaseBp) : p.healBp;
    // (The board draws it from the beat itself: no event every second.)
    for (const o of s.enemies) {
      if (o === e || o.hp <= 0 || o.hp >= o.maxHp || o.kind === 'boneWall' || !withinRange(e.x, e.y, o.x, o.y, p.radius)) continue;
      o.hp += imin(o.maxHp - o.hp, imax(1, mulBp(o.maxHp, bp)));
    }
  },
  // A nightmare falling near him leaves him its soul.
  died: (s, e, u, dead) => {
    const so = LUCAN.souls;
    if (dead.kind === 'boneWall' || (u.k[SOULS] as number) >= so.max || !withinRange(e.x, e.y, dead.x, dead.y, so.radius)) return;
    u.k[SOULS] = (u.k[SOULS] as number) + 1;
    ev(s, { t: 'beam', x: dead.x, y: dead.y, tx: e.x, ty: e.y, kind: 'lucanSoul' });
  },
};
